import { ChessTermType, TermAction, } from "@chesshotel/types";
import { Chess, PAWN, WHITE, BLACK } from "@chesshotel/chess";
import { getFen, exchangeEvaluation, pieceValues, uci2move, } from "./util.js";
import { isValidFenMove } from "./tactics/util.js";
export function detectThreat(fen, move) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(move));
    if (!moveResult) {
        return null;
    }
    if (chess.inCheck()) {
        return null;
    }
    const exchange = exchangeEvaluation(chess.fen(), moveResult.to);
    if (exchange.gain > 0 ||
        (exchange.gain == 0 &&
            exchange.moveSequence.length > 0 &&
            moveResult.piece == PAWN)) {
        return null;
    }
    chess.changeTurn();
    const nextMoves = chess.moves({
        square: moveResult.to,
        piece: moveResult.piece,
        verbose: true,
    });
    const attacks = nextMoves
        .filter((move) => move.captured)
        .map((move) => {
        const from = chess.getBoardPiece(move.from);
        const to = chess.getBoardPiece(move.to);
        return { from, to };
    });
    if (attacks.length === 0) {
        return null;
    }
    const vulnerablePieces = attacks
        .map((attack) => {
        const { to } = attack;
        const evaluation = exchangeEvaluation(chess.fen(), to.square, {
            backtrackBadMoves: true,
            backtrackKeepFirstMove: true,
        });
        return { attack, evaluation };
    })
        .filter(({ evaluation }) => evaluation.gain > 0)
        .sort((a, b) => b.evaluation.gain - a.evaluation.gain);
    if (vulnerablePieces.length === 0) {
        return null;
    }
    const attackedPiece = vulnerablePieces[0].attack.to;
    const maxGain = vulnerablePieces[0].evaluation.gain;
    const evalResult = vulnerablePieces[0].evaluation;
    let mainPiece = {
        type: moveResult.piece,
        square: moveResult.to,
        color: moveResult.color,
    };
    let involvedPiece = null;
    let term = null;
    if (moveResult.piece === PAWN && attackedPiece.type !== PAWN) {
        term = ChessTermType.KICK_PIECE;
    }
    else if (pieceValues[attackedPiece.type] > pieceValues[moveResult.piece]) {
        term = ChessTermType.TEMPO;
    }
    else {
        term = ChessTermType.THREATEN_PIECE;
        involvedPiece = vulnerablePieces[1]?.attack?.to;
    }
    const arrows = [
        { from: moveResult.from, to: moveResult.to },
        { from: moveResult.to, to: attackedPiece.square },
    ];
    const translationKeys = {
        piece: mainPiece.type,
        piece2: attackedPiece.type,
    };
    return {
        term,
        mainPiece,
        attackedPiece,
        involvedPiece,
        move: moveResult,
        maxGain: maxGain * 0.5,
        arrows,
        translationKeys,
        evalResult,
    };
}
function getPvMoves(pvMoves, evalResult) {
    const movesLength = evalResult.moveSequence.length;
    return movesLength > 2 ? pvMoves.slice(0, movesLength) : null;
}
function getMove(move, fen) {
    const chess = new Chess(fen);
    const moveResult = chess.move(uci2move(move));
    return moveResult;
}
export function detectNegativeThreat(position, lastPosition) {
    const userMoveThreat = detectThreat(lastPosition.fen, position.move);
    const bestMoveThreat = detectThreat(lastPosition.fen, lastPosition.bestMove);
    if (bestMoveThreat) {
        const move = getMove(position.move, lastPosition.fen);
        if (move?.captured && bestMoveThreat.attackedPiece.square === move.to) {
            return null;
        }
    }
    if (bestMoveThreat && !userMoveThreat) {
        return {
            ...bestMoveThreat,
            termAction: TermAction.MISS,
            pvMoves: getPvMoves(lastPosition.pvMoves, bestMoveThreat.evalResult),
        };
    }
    if (bestMoveThreat && userMoveThreat) {
        const termAction = bestMoveThreat.attackedPiece.type === userMoveThreat.attackedPiece.type
            ? TermAction.BETTER_WAY
            : TermAction.MISS;
        return {
            ...bestMoveThreat,
            termAction,
            term: ChessTermType.THREATEN_PIECE,
            pvMoves: getPvMoves(lastPosition.pvMoves, bestMoveThreat.evalResult),
        };
    }
    const opponentMove = position.pvMoves[0];
    const user = detectThreat(position.fen, opponentMove);
    if (user) {
        const bestFen = getFen(lastPosition.fen, lastPosition.pvMoves[0]);
        const isValid = isValidFenMove(bestFen, opponentMove);
        const best = isValid && detectThreat(bestFen, opponentMove);
        if (!best) {
            const color = position.moveIndex % 2 == 1 ? WHITE : BLACK;
            return {
                ...user,
                translationKeys: { ...user.translationKeys, color },
                termAction: TermAction.ALLOW,
                pvMoves: getPvMoves(position.pvMoves, user.evalResult),
            };
        }
    }
}
export function detectPositiveThreat(position, lastPosition) {
    const threat = detectThreat(lastPosition.fen, position.move);
    if (threat) {
        const lastArrow = threat.arrows.pop();
        threat.arrows = [lastArrow];
        return {
            ...threat,
            termAction: TermAction.PERFORM,
        };
    }
}
//# sourceMappingURL=attacks.js.map